import React, { useState } from 'react';
import { Volume2, Mic, Sparkles, Plus, Play, RotateCcw, MessageSquare, UserCheck, History, CheckCircle2 } from 'lucide-react';
import { DEMO_STUDENTS } from '../../data/demoStudents';

export const AACModule: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(DEMO_STUDENTS[0].id);
  const [selectedPhrase, setSelectedPhrase] = useState<string[]>([]);
  const [pitch, setPitch] = useState(1);
  const [rate, setRate] = useState(1);
  const [language, setLanguage] = useState('en-US');
  const [phraseHistory, setPhraseHistory] = useState<string[]>([
    'I Want Water',
    'Help Please',
    'Happy Today'
  ]);
  const [customWordInput, setCustomWordInput] = useState('');

  const activeStudent = DEMO_STUDENTS.find(s => s.id === selectedStudentId) || DEMO_STUDENTS[0];

  const symbols = [
    { id: 'want', label: 'I Want', emoji: '🖐️', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { id: 'help', label: 'Help', emoji: '🆘', color: 'bg-red-100 text-red-800 border-red-300' },
    { id: 'water', label: 'Water', emoji: '💧', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
    { id: 'food', label: 'Food / Eat', emoji: '🍎', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    { id: 'happy', label: 'Happy', emoji: '😊', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    { id: 'pain', label: 'Pain / Hurt', emoji: '🤕', color: 'bg-rose-100 text-rose-800 border-rose-300' },
    { id: 'toilet', label: 'Restroom', emoji: '🚻', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    { id: 'play', label: 'Play / Activity', emoji: '🧩', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
    { id: 'yes', label: 'Yes', emoji: '✅', color: 'bg-green-100 text-green-800 border-green-300' },
    { id: 'no', label: 'No', emoji: '❌', color: 'bg-slate-200 text-slate-800 border-slate-300' },
    { id: 'stop', label: 'Stop', emoji: '🛑', color: 'bg-red-200 text-red-900 border-red-400' },
    { id: 'more', label: 'More', emoji: '➕', color: 'bg-sky-100 text-sky-800 border-sky-300' },
  ];

  const handleSpeak = (text: string) => {
    if (!text.trim()) return;
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.lang = language;
      window.speechSynthesis.speak(utterance);
    }
    setPhraseHistory(prev => [text, ...prev.filter(p => p !== text)].slice(0, 8));
  };

  const handleAddSymbol = (label: string) => {
    setSelectedPhrase((prev) => [...prev, label]);
    handleSpeak(label);
  };

  const handleAddCustomWord = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customWordInput.trim()) return;
    handleAddSymbol(customWordInput.trim());
    setCustomWordInput('');
  };

  const handleClear = () => {
    setSelectedPhrase([]);
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      
      {/* Pre-Loaded Demo Student Selector Banner */}
      <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="font-bold text-slate-800">Pre-Loaded Active Demo Student:</span>
          <select 
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            className="px-2.5 py-1 bg-white border border-blue-300 rounded-lg font-bold text-blue-900 focus:ring-2 focus:ring-blue-500"
          >
            {DEMO_STUDENTS.map(st => (
              <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
            ))}
          </select>
        </div>

        <div className="text-[11px] text-blue-800 font-semibold truncate">
          Active IEP Target: <span className="font-bold">{activeStudent.iepGoal}</span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
            Tobii Dynavox / TD Snap Benchmark
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">AAC Augmentative Speech Synthesizer</h2>
          <p className="text-xs text-slate-500">Real-time symbol communication board for {activeStudent.name}</p>
        </div>

        {/* Audio Controls */}
        <div className="flex items-center gap-4 bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-xs">
          <div>
            <label className="block text-[10px] font-bold text-slate-500">Voice Language</label>
            <select 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-white border border-slate-300 rounded px-2 py-1 text-xs font-medium"
            >
              <option value="en-US">English (US)</option>
              <option value="hi-IN">Hindi (India)</option>
              <option value="mr-IN">Marathi (India)</option>
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500">Speed: {rate}x</label>
            <input 
              type="range" min="0.5" max="1.5" step="0.1" 
              value={rate} onChange={(e) => setRate(parseFloat(e.target.value))}
              className="w-20 accent-blue-600"
            />
          </div>
        </div>
      </div>

      {/* Selected Phrase Builder Box */}
      <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center justify-between gap-4 min-h-[70px] shadow-inner">
        <div className="flex items-center gap-2 flex-wrap">
          <MessageSquare className="w-5 h-5 text-blue-400 shrink-0" />
          {selectedPhrase.length === 0 ? (
            <span className="text-slate-400 italic text-sm">Tap symbols below to build a speech phrase...</span>
          ) : (
            selectedPhrase.map((word, idx) => (
              <span key={idx} className="px-3 py-1 bg-blue-600 text-white font-bold rounded-lg text-sm shadow">
                {word}
              </span>
            ))
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => handleSpeak(selectedPhrase.join(' '))}
            disabled={selectedPhrase.length === 0}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition"
          >
            <Play className="w-4 h-4 fill-current" />
            Speak Phrase
          </button>
          <button
            onClick={handleClear}
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs"
            title="Clear Phrase"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Add Custom Word Form */}
      <form onSubmit={handleAddCustomWord} className="flex items-center gap-2">
        <input
          type="text"
          value={customWordInput}
          onChange={(e) => setCustomWordInput(e.target.value)}
          placeholder="Add custom word to phrase..."
          className="flex-1 px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl flex items-center gap-1 shadow"
        >
          <Plus className="w-4 h-4" /> Add Word
        </button>
      </form>

      {/* Symbol Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {symbols.map((sym) => (
          <button
            key={sym.id}
            onClick={() => handleAddSymbol(sym.label)}
            className={`p-4 rounded-xl border-2 ${sym.color} flex flex-col items-center justify-center gap-2 shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition text-center`}
          >
            <span className="text-3xl">{sym.emoji}</span>
            <span className="text-xs font-extrabold">{sym.label}</span>
          </button>
        ))}
      </div>

      {/* Phrase History Log */}
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
        <div className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
          <History className="w-4 h-4 text-blue-600" />
          Recent TTS Spoken Phrase History for {activeStudent.name}:
        </div>
        <div className="flex flex-wrap gap-2">
          {phraseHistory.map((phrase, idx) => (
            <button
              key={idx}
              onClick={() => handleSpeak(phrase)}
              className="px-3 py-1 bg-white hover:bg-blue-50 border border-slate-200 text-slate-800 font-bold text-xs rounded-lg transition flex items-center gap-1 shadow-sm"
            >
              <Volume2 className="w-3.5 h-3.5 text-blue-600" />
              <span>{phrase}</span>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};
